import { makeAutoObservable, runInAction } from "mobx";
import { CreateProject, Project, UpdateProject } from "../models/Project";
import { RootStore } from "../../shared/stores/RootStore";
import { ProjectApi } from "../services/ProjectApi";
import { compileAllPages } from "../../shared/utils/http-utils";
import { DistanceUnit } from "@keita/lanepatrol-sdk";

interface MapSelection {
  x: number;
  y: number;
  projects: Project[];
}
export class ProjectStore {
  rootStore;
  projects: Project[] = [];
  selectedId?: string;

  distanceUnit: DistanceUnit = DistanceUnit.Metric;
  rightHandTraffic = true;

  mapSelection: MapSelection | undefined;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.projects = [];
      this.selectedId = undefined;
    });
  }

  get speedUnitStr() {
    return this.distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h";
  }

  get distanceUnitStr() {
    return this.distanceUnit === DistanceUnit.Imperial ? "mi" : "km";
  }

  private setProjects(projects: Project[]) {
    runInAction(() => {
      this.projects = projects;
    });

    const { mapStore } = this.rootStore;
    mapStore.projectsMapSource?.eventProjectsUpdated();
  }

  get(projectId?: string) {
    if (!projectId) return;
    return this.projects.find((project) => project.id === projectId);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(projectId?: string) {
    runInAction(() => (this.selectedId = projectId));
    this.updateProjectSettings();
  }

  updateProjectSettings() {
    const project = this.getSelected();
    if (project) {
      runInAction(() => {
        this.distanceUnit = project.distanceUnit;
        this.rightHandTraffic = project.rightHandTraffic;
      });
    }
  }

  setMapSelection(x: number, y: number, projectIds: string[]) {
    runInAction(() => {
      this.mapSelection = {
        x,
        y,
        projects: this.projects.filter((p) => projectIds.includes(p.id)),
      };
    });
  }

  clearMapSelection() {
    runInAction(() => {
      this.mapSelection = undefined;
    });
  }

  async fetchSelectedProject(projectId: string, cancelIf?: () => boolean) {
    const { data: project } = await ProjectApi.get(projectId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.setProjects([project]);
      this.selectedId = project.id;
    });
    this.updateProjectSettings();
  }

  async addProject(newProj: CreateProject) {
    const response = await ProjectApi.create(newProj);
    await this.fetchAllProjects();
    return response.data;
  }

  async fetchAllProjects(cancelIf?: () => boolean) {
    const projects = await compileAllPages(
      async (pageNumber) => await ProjectApi.page(20, pageNumber),
    );
    if (cancelIf?.()) return;

    this.setProjects(projects);
    this.updateProjectSettings();
  }

  async updateProject(body: UpdateProject, id: string) {
    const response = await ProjectApi.update(body, id);
    this.setProjects(
      this.projects.map((p) => (p.id === id ? response.data : p)),
    );
    this.updateProjectSettings();
  }

  async deleteProject(projectId: string) {
    await ProjectApi.delete(projectId);
    this.setProjects(this.projects.filter((p) => p.id !== projectId));
  }

  async removeFromLibrary(projectId: string) {
    await ProjectApi.deleteMyAccess(projectId);
    this.setProjects(this.projects.filter((p) => p.id !== projectId));
  }
}
